import { Box, Typography } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';

import Section from 'components/shared/Section';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { DealsService } from 'service/deals.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setOwner, setStage } from 'store/slices/dealsSlice';
import zeplinColor from 'theme/Color';
import DealDropdown, { OwnerOption } from '../../DealDropdown';
import { muiStyles } from './muiStyles';

export type OptionType = { value: string; label: string };

const DetailSection = ({ data, label }: { data: any; label: string }) => {
  const dispatch = useAppDispatch();
  const { owners, stages, stage, owner } = useAppSelector((state) => state.deals);
  const { dealId } = useAppSelector((state) => state.deals.deal);

  const { startOperation, alertState, setAlertState, getAlertContent, completeOperation } =
    useAlertStatus();

  const handleFieldChange = async (fieldName: string, newValue: any) => {
    if (!dealId) return;

    try {
      startOperation('update', 'Updating deal...');
      if (fieldName === 'ownerName') {
        await DealsService.updateDeal(dealId, { owner: newValue });
      } else {
        await DealsService.updateDeal(dealId, { [fieldName]: newValue });
      }

      completeOperation('update', 'Deal updated successfully.');
    } catch (error: any) {
      completeOperation('update', error?.response?.data?.message || 'Failed to update deal', true);
    }
  };

  const handleOwnerChange = (fieldName: string, value: OwnerOption | null) => {
    if (value === null) return;

    dispatch(setOwner(value));
    handleFieldChange(fieldName, value);
  };

  const handleStageChange = (fieldName: string, value: string | null) => {
    if (value === null) return;

    dispatch(setStage(value));
    handleFieldChange(fieldName, value);
  };

  return (
    <Section title={label}>
      <Box sx={muiStyles.contentGrid}>
        {data.map((item: any) => (
          <Box key={item.label} sx={muiStyles.gridItem}>
            <Box>
              {item.fieldName === 'stageName' && (
                <DealDropdown
                  options={stages}
                  value={stage}
                  setValue={handleStageChange}
                  fieldName='stageName'
                  isBgDark={false}
                  isChip
                />
              )}
              {item.fieldName === 'ownerName' && (
                <DealDropdown
                  options={owners}
                  value={owner}
                  setValue={handleOwnerChange}
                  fieldName='ownerName'
                  isBgDark={false}
                />
              )}

              {!item.isSelect && (
                <Typography variant='responsiveHeading5' sx={{ color: zeplinColor.BasicBlack }}>
                  {item.format && item.value ? item.format(item.value) : item.value}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral8 }}>
                {item.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Section>
  );
};

export default DetailSection;
